import React, { useRef, useEffect, useCallback } from "react";
import * as d3 from "d3";
import {
  buildTooltipHtml,
  createTooltipHandlers,
  formatTooltipNumber,
  removeTooltip,
} from "../../common/components/d3/d3ChartShared";

const PIE_COLORS = [
  "#5f9fcf",
  "#d48a4f",
  "#63a66f",
  "#d36a6a",
  "#8f7cab",
  "#9c7b63",
  "#5fbaa8",
  "#b1a34a",
  "#8c95a3",
  "#7c99d1",
  "#71767f",
];

const PieChart = ({ data, title, groupBy }) => {
  const chartRef = useRef(null);
  const tooltipRef = useRef(null);

  const renderChart = useCallback(() => {
    const innerChartRef = chartRef;
    if (!innerChartRef.current) {
      return;
    }

    const margin = { top: 44, right: 160, bottom: 30, left: 24 };
    const containerWidth = innerChartRef.current.offsetWidth;
    const containerHeight = 300;
    const width = containerWidth - margin.left - margin.right;
    const height = containerHeight - margin.top - margin.bottom;
    const radius = Math.min(width, height) / 2;

    d3.select(innerChartRef.current).select("svg").remove();
    removeTooltip(tooltipRef);

    if (!width || width <= 0 || !height || height <= 0) {
      return;
    }

    const svg = d3
      .select(innerChartRef.current)
      .append("svg")
      .attr("width", "100%")
      .attr("height", "100%")
      .attr(
        "viewBox",
        `0 0 ${width + margin.left + margin.right} ${
          height + margin.top + margin.bottom
        }`,
      )
      .attr("preserveAspectRatio", "xMinYMin meet")
      .append("g")
      .attr(
        "transform",
        `translate(${Math.max(radius + margin.left, width / 2 - margin.right / 3)}, ${(height + margin.top + margin.bottom) / 2})`,
      );

    const dataMap = data.reduce((acc, item) => {
      const key = item[groupBy];
      if (!acc[key]) {
        acc[key] = 0;
      }
      acc[key] += item.Quantity;
      return acc;
    }, {});

    const pieData = Object.entries(dataMap)
      .map(([key, value]) => ({ key, value }))
      .sort((a, b) => b.value - a.value);

    const topNData = pieData.slice(0, 10);
    const others = pieData.slice(10).reduce((acc, cur) => acc + cur.value, 0);
    if (others > 0) {
      topNData.push({ key: "All Others", value: others });
    }

    const pie = d3
      .pie()
      .value((d) => d.value)
      .sort(null);

    const arc = d3.arc().innerRadius(radius * 0.46).outerRadius(radius * 0.9);
    const hoverArc = d3.arc().innerRadius(radius * 0.46).outerRadius(radius * 0.94);

    const color = d3
      .scaleOrdinal()
      .domain(topNData.map((d) => d.key))
      .range(PIE_COLORS);

    const total = d3.sum(topNData, (d) => d.value);

    const { showTooltip, hideTooltip } = createTooltipHandlers({
      tooltipRef,
    });

    const arcs = svg
      .selectAll(".arc")
      .data(pie(topNData))
      .enter()
      .append("g")
      .attr("class", "arc");

    arcs
      .append("path")
      .attr("class", "dashboard-pie-slice")
      .attr("d", arc)
      .attr("fill", (d) => color(d.data.key))
      .on("mouseover", function (event, d) {
        d3.select(this).transition().duration(120).attr("d", hoverArc);
        showTooltip(
          event,
          buildTooltipHtml({
            title: d.data.key,
            rows: [
              {
                label: "Quantity",
                value: formatTooltipNumber(d.data.value, {
                  maximumFractionDigits: 0,
                }),
                color: color(d.data.key),
              },
              {
                label: "Share",
                value: `${formatTooltipNumber((d.data.value / total) * 100, {
                  maximumFractionDigits: 1,
                })}%`,
                color: color(d.data.key),
              },
            ],
          }),
        );
      })
      .on("mousemove", function (event, d) {
        showTooltip(
          event,
          buildTooltipHtml({
            title: d.data.key,
            rows: [
              {
                label: "Quantity",
                value: formatTooltipNumber(d.data.value, {
                  maximumFractionDigits: 0,
                }),
                color: color(d.data.key),
              },
              {
                label: "Share",
                value: `${formatTooltipNumber((d.data.value / total) * 100, {
                  maximumFractionDigits: 1,
                })}%`,
                color: color(d.data.key),
              },
            ],
          }),
        );
      })
      .on("mouseout", function () {
        d3.select(this).transition().duration(120).attr("d", arc);
        hideTooltip();
      });

    svg
      .append("text")
      .attr("x", 0)
      .attr("y", -height / 2 - margin.top / 2)
      .attr("text-anchor", "middle")
      .style("font-size", "16px")
      .attr("class", "d3-chart-title")
      .text(title);

    svg
      .append("text")
      .attr("class", "dashboard-pie-total")
      .attr("text-anchor", "middle")
      .attr("y", -4)
      .text(formatTooltipNumber(total, { maximumFractionDigits: 0 }));

    svg
      .append("text")
      .attr("class", "dashboard-pie-total-label")
      .attr("text-anchor", "middle")
      .attr("y", 14)
      .text("Total");

    const legend = svg
      .append("g")
      .attr("transform", `translate(${radius + margin.left - 8}, ${-height / 2})`)
      .selectAll(".legend")
      .data(topNData)
      .enter()
      .append("g")
      .attr("class", "legend")
      .attr("transform", (d, i) => `translate(0, ${i * 22})`);

    legend
      .append("rect")
      .attr("x", 0)
      .attr("width", 18)
      .attr("height", 18)
      .attr("rx", 4)
      .attr("ry", 4)
      .attr("class", "dashboard-pie-legend-swatch")
      .attr("fill", (d) => color(d.key));

    const wrapText = (text, textWidth) => {
      text.each(function () {
        const textElement = d3.select(this);
        const words = textElement.text().split(/\s+/).reverse();
        const lineHeight = 1.1;
        const xPosition = textElement.attr("x");
        const yPosition = textElement.attr("y");
        const dy = parseFloat(textElement.attr("dy")) - lineHeight / 2 || 0;
        let lineNumber = 0;
        let line = [];
        let word;
        let tspan = textElement
          .text(null)
          .append("tspan")
          .attr("x", xPosition)
          .attr("y", yPosition)
          .attr("dy", `${dy}em`);

        while ((word = words.pop())) {
          line.push(word);
          tspan.text(line.join(" "));
          if (tspan.node().getComputedTextLength() > textWidth) {
            line.pop();
            if (lineNumber < 1) {
              tspan.text(line.join(" "));
              line = [word];
              tspan = textElement
                .append("tspan")
                .attr("x", xPosition)
                .attr("y", yPosition)
                .attr("dy", `${++lineNumber * lineHeight + dy}em`)
                .text(word);
            }
          }
        }
      });
    };

    legend
      .append("text")
      .attr("x", 24)
      .attr("y", 9)
      .attr("dy", ".35em")
      .style("text-anchor", "start")
      .attr("class", "d3-chart-legend-label")
      .style("font-size", "9px")
      .text((d) => d.key)
      .call(wrapText, 160);
  }, [data, title, groupBy]);

  useEffect(() => {
    requestAnimationFrame(renderChart);

    return () => {
      removeTooltip(tooltipRef);
    };
  }, [renderChart]);

  useEffect(() => {
    const innerChartRef = chartRef;
    let resizeTimeout = null;

    const throttledResize = () => {
      if (!resizeTimeout) {
        resizeTimeout = setTimeout(() => {
          renderChart();
          resizeTimeout = null;
        }, 100);
      }
    };

    const observer = new ResizeObserver(throttledResize);

    if (innerChartRef.current) {
      observer.observe(innerChartRef.current);
    }

    return () => {
      if (innerChartRef.current) {
        observer.unobserve(innerChartRef.current);
      }
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
        resizeTimeout = null;
      }
    };
  }, [renderChart]);

  return (
    <div
      ref={chartRef}
      className="dashboard-d3-chart"
      style={{ width: "100%", height: "100%" }}
    ></div>
  );
};

export default PieChart;
